<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import ReportHeader from './components/ReportHeader.vue';
import ReportFilterSelector from './components/FilterSelector.vue';
import ReportMetricCard from './components/ReportMetricCard.vue';
import AgentProductivityTable from './components/tables/AgentProductivityTable.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportsAPI from 'dashboard/api/reports';
import { generateFileName } from 'dashboard/helper/downloadHelper';

const store = useStore();

const { t } = useI18n();

const from = ref(0);
const to = ref(0);
const inboxIds = ref([]);

const avgQueueTimeSeconds = ref(null);
const totalConversations = ref(null);
const agentsOnline = ref(null);

const agentsData = ref([]);

const isLoading = ref(false);

const formatTime = seconds => {
  if (!seconds) return '--';
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return `${hours}h ${mins}m`;
};

const formattedQueueTime = computed(() =>
  formatTime(avgQueueTimeSeconds.value)
);

const formattedTotalConversations = computed(() =>
  totalConversations.value != null
    ? totalConversations.value.toLocaleString()
    : '--'
);

const formattedAgentsOnline = computed(() =>
  agentsOnline.value != null ? agentsOnline.value.toString() : '--'
);

const tableData = computed(() =>
  agentsData.value.map(agent => ({
    name: agent.name,
    email: agent.email,
    thumbnail: agent.thumbnail,
    status: agent.availability_status,
    open: agent.open_conversations,
    closed: agent.closed_conversations,
    tmo: agent.avg_operation_time || null,
    csat: agent.csat_percentage,
  }))
);

const fetchData = async () => {
  if (!from.value || !to.value) return;

  isLoading.value = true;
  try {
    const response = await ReportsAPI.getQueuesAgentsReport({
      from: from.value,
      to: to.value,
      inboxIds: inboxIds.value,
    });

    const { metrics, agents } = response.data;

    avgQueueTimeSeconds.value = metrics.avg_queue_time;
    totalConversations.value = metrics.total_conversations;
    agentsOnline.value = metrics.agents_online;
    agentsData.value = agents || [];
  } catch (error) {
    useAlert(t('QUEUES_AGENTS_REPORTS.API_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const onDownloadClick = () => {
  const fileName = generateFileName({ type: 'queues_agents', to: to.value });
  store.dispatch('downloadQueuesAgentsReports', {
    from: from.value,
    to: to.value,
    inboxIds: inboxIds.value,
    fileName,
  });
};

const onFilterChange = ({ from: fromDate, to: toDate, selectedInboxes }) => {
  from.value = fromDate;
  to.value = toDate;
  inboxIds.value = selectedInboxes?.map(el => el.id) || [];

  fetchData();
};
</script>

<template>
  <ReportHeader :header-title="t('QUEUES_AGENTS_REPORTS.HEADER')">
    <V4Button
      :label="t('QUEUES_AGENTS_REPORTS.DOWNLOAD')"
      icon="i-ph-download-simple"
      size="sm"
      @click="onDownloadClick"
    />
  </ReportHeader>

  <div class="flex flex-col gap-4">
    <!-- Filters -->
    <ReportFilterSelector
      show-inbox-filter
      :show-business-hours-switch="false"
      @filter-change="onFilterChange"
    />

    <!-- Metric Cards -->
    <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <ReportMetricCard
          :label="t('QUEUES_AGENTS_REPORTS.METRIC.AVG_QUEUE_TIME.LABEL')"
          :value="formattedQueueTime"
          :info-text="t('QUEUES_AGENTS_REPORTS.METRIC.AVG_QUEUE_TIME.TOOLTIP')"
        />
      </div>
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <ReportMetricCard
          :label="t('QUEUES_AGENTS_REPORTS.METRIC.TOTAL_CONVERSATIONS.LABEL')"
          :value="formattedTotalConversations"
          :info-text="
            t('QUEUES_AGENTS_REPORTS.METRIC.TOTAL_CONVERSATIONS.TOOLTIP')
          "
        />
      </div>
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <ReportMetricCard
          :label="t('QUEUES_AGENTS_REPORTS.METRIC.AGENTS_ONLINE.LABEL')"
          :value="formattedAgentsOnline"
          :info-text="t('QUEUES_AGENTS_REPORTS.METRIC.AGENTS_ONLINE.TOOLTIP')"
        />
      </div>
    </div>

    <!-- Agent Productivity Table -->
    <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
      <h3 class="mb-4 text-base font-medium text-n-slate-12">
        {{ t('QUEUES_AGENTS_REPORTS.TABLE.TITLE') }}
      </h3>
      <AgentProductivityTable :data="tableData" :is-loading="isLoading" />
    </div>
  </div>
</template>
